<script setup>
import { computed, ref } from 'vue';
import { getInboxIconByType } from 'dashboard/helper/inbox';
import { useRouter, useRoute } from 'vue-router';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper.js';
import { dynamicTime, shortTimestamp } from 'shared/helpers/timeHelper';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import CardMessagePreview from './CardMessagePreview.vue';
import CardMessagePreviewWithMeta from './CardMessagePreviewWithMeta.vue';
import CardPriorityIcon from './CardPriorityIcon.vue';
// [AstraChat] status do ticket no histórico do contato (item 14) — reusa o ícone
// de status da lista principal (open/pending/resolved/snoozed) com tooltip.
import CardStatusIcon from './CardStatusIcon.vue';
import { useKanbanStageBadge } from 'dashboard/composables/useKanbanStageBadge';

const props = defineProps({
  conversation: {
    type: Object,
    required: true,
  },
  contact: {
    type: Object,
    required: true,
  },
  stateInbox: {
    type: Object,
    required: true,
  },
  accountLabels: {
    type: Array,
    required: true,
  },
});

const router = useRouter();
const route = useRoute();

const cardMessagePreviewWithMetaRef = ref(null);

const currentContact = computed(() => props.contact);

const currentContactName = computed(() => currentContact.value?.name);
const currentContactThumbnail = computed(() => currentContact.value?.thumbnail);
const currentContactStatus = computed(
  () => currentContact.value?.availabilityStatus
);

const inbox = computed(() => props.stateInbox);

const inboxName = computed(() => inbox.value?.name);

const inboxIcon = computed(() => {
  const { channelType, medium, voiceEnabled } = inbox.value;
  return getInboxIconByType(channelType, medium, 'fill', voiceEnabled);
});

const lastActivityAt = computed(() => {
  const timestamp = props.conversation?.timestamp;
  return timestamp ? shortTimestamp(dynamicTime(timestamp)) : '';
});

// Badge de funil/etapa do Kanban (se a conversa tiver um card)
const { stageFor } = useKanbanStageBadge();
const kanbanStage = computed(() => stageFor(props.conversation?.id));

// A v4.17.0 EXTRAIU esta condição para `hasVisibleLabels`; o
// showMessagePreviewWithoutMeta agora vive logo abaixo e consome ela. Manter o
// nome antigo aqui criaria declaração duplicada.
const hasVisibleLabels = computed(() => {
  const { labels = [] } = props.conversation;
  return props.accountLabels.some(({ title }) => labels.includes(title));
});

const showMessagePreviewWithoutMeta = computed(() => {
  return (
    !cardMessagePreviewWithMetaRef.value?.hasSlaThreshold &&
    !hasVisibleLabels.value
  );
});

const onCardClick = e => {
  const path = frontendURL(
    conversationUrl({
      accountId: route.params.accountId,
      id: props.conversation.id,
    })
  );

  if (e.metaKey || e.ctrlKey) {
    window.open(
      window.chatwootConfig.hostURL + path,
      '_blank',
      'noopener noreferrer nofollow'
    );
    return;
  }
  router.push({ path });
};
</script>

<template>
  <div
    role="button"
    class="flex w-full gap-3 px-3 py-4 transition-all duration-300 ease-in-out cursor-pointer"
    @click="onCardClick"
  >
    <Avatar
      :name="currentContactName"
      :src="currentContactThumbnail"
      :size="24"
      :status="currentContactStatus"
      rounded-full
    />
    <div class="flex flex-col w-full gap-1 min-w-0">
      <div class="flex items-center justify-between h-6 gap-2">
        <h4 class="text-base font-medium truncate text-n-slate-12">
          {{ currentContactName }}
        </h4>
        <div class="flex items-center gap-2">
          <CardStatusIcon :status="conversation.status" />
          <CardPriorityIcon :priority="conversation.priority || null" />
          <div
            v-tooltip.left="inboxName"
            class="flex items-center justify-center flex-shrink-0 rounded-full bg-n-alpha-2 size-5"
          >
            <Icon
              :icon="inboxIcon"
              class="flex-shrink-0 text-n-slate-11 size-3"
            />
          </div>
          <span class="text-sm text-n-slate-10">
            {{ lastActivityAt }}
          </span>
        </div>
      </div>
      <CardMessagePreview
        v-show="showMessagePreviewWithoutMeta"
        :conversation="conversation"
      />
      <CardMessagePreviewWithMeta
        v-show="!showMessagePreviewWithoutMeta"
        ref="cardMessagePreviewWithMetaRef"
        :conversation="conversation"
        :contact="contact"
        :account-labels="accountLabels"
        :has-labels="hasVisibleLabels"
      />
      <div v-if="kanbanStage" class="flex items-center gap-1 mt-0.5 min-w-0">
        <span
          v-tooltip="kanbanStage.funnel_name"
          class="px-1.5 py-px text-[0.625rem] font-medium leading-tight truncate rounded-full max-w-[85px]"
          :style="{
            backgroundColor: `${kanbanStage.funnel_color || '#6366f1'}22`,
            color: kanbanStage.funnel_color || '#6366f1',
          }"
        >
          {{ kanbanStage.funnel_name }}
        </span>
        <span class="text-[0.5625rem] text-n-slate-9 shrink-0">›</span>
        <span
          v-tooltip="kanbanStage.stage_name"
          class="px-1.5 py-px text-[0.625rem] font-medium leading-tight truncate rounded-full max-w-[85px]"
          :style="{
            backgroundColor: `${kanbanStage.stage_color || '#6B7280'}22`,
            color: kanbanStage.stage_color || '#6B7280',
          }"
        >
          {{ kanbanStage.stage_name }}
        </span>
      </div>
    </div>
  </div>
</template>
